import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ChevronLeft,
  Search,
  Eye,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
  paid: { label: 'Paid', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  processing: { label: 'Processing', icon: Package, color: 'bg-blue-100 text-blue-700' },
  shipped: { label: 'Shipped', icon: Truck, color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-red-100 text-red-700' },
}

export default function AdminOrders() {
  const { isAuthenticated, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedOrder, setSelectedOrder] = useState(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account')
      return
    }
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchOrders()
  }, [isAuthenticated, isAdmin])

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (*)
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      setOrders(data || [])
    } catch (error) {
      console.error('Error fetching orders:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId)

      if (error) throw error

      setOrders(
        orders.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      )
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus })
      }
    } catch (error) {
      console.error('Error updating order status:', error)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus =
      statusFilter === 'all' || order.status === statusFilter
    return matchesSearch && matchesStatus
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading orders...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-linen">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <h1 className="font-display text-display-md text-pp-ink">Orders</h1>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-pp-sand/50 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pp-stone" />
              <input
                type="text"
                placeholder="Search by order # or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink placeholder-pp-stone/60 focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-2xl border border-pp-sand/50 overflow-hidden">
          {filteredOrders.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-pp-stone mx-auto mb-4" />
              <p className="text-pp-stone">No orders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-pp-linen/50 border-b border-pp-sand/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Order
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Total
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-pp-stone">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-pp-stone">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pp-sand/30">
                  {filteredOrders.map((order) => {
                    const status = statusConfig[order.status] || statusConfig.pending
                    const StatusIcon = status.icon
                    return (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="hover:bg-pp-ivory/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium text-pp-ink">
                            {order.order_number}
                          </p>
                          <p className="text-xs text-pp-stone">
                            {order.order_items?.length || 0} items
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-pp-charcoal">{order.customer_email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-pp-stone">
                            {formatDate(order.created_at)}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-pp-ink">
                            {formatPrice(order.total)}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.color}`}
                          >
                            <StatusIcon className="w-3.5 h-3.5" />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="p-2 text-pp-stone hover:text-pp-terracotta transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setSelectedOrder(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-pp-sand/50">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-xl text-pp-ink">
                    Order {selectedOrder.order_number}
                  </h2>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="text-pp-stone hover:text-pp-charcoal"
                  >
                    &times;
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Status Update */}
                <div>
                  <label className="block text-sm font-medium text-pp-charcoal mb-2">
                    Update Status
                  </label>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) =>
                      updateOrderStatus(selectedOrder.id, e.target.value)
                    }
                    className="w-full px-4 py-2 border border-pp-sand rounded-lg"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Customer Info */}
                <div>
                  <h3 className="font-medium text-pp-ink mb-2">Customer</h3>
                  <p className="text-pp-stone">{selectedOrder.customer_email}</p>
                </div>

                {/* Shipping Address */}
                {selectedOrder.shipping_address && (
                  <div>
                    <h3 className="font-medium text-pp-ink mb-2">
                      Shipping Address
                    </h3>
                    <div className="text-sm text-pp-stone">
                      <p>{selectedOrder.shipping_address.name}</p>
                      <p>{selectedOrder.shipping_address.line1}</p>
                      {selectedOrder.shipping_address.line2 && (
                        <p>{selectedOrder.shipping_address.line2}</p>
                      )}
                      <p>
                        {selectedOrder.shipping_address.city},{' '}
                        {selectedOrder.shipping_address.state}{' '}
                        {selectedOrder.shipping_address.postal_code}
                      </p>
                    </div>
                  </div>
                )}

                {/* Order Items */}
                <div>
                  <h3 className="font-medium text-pp-ink mb-4">Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.order_items?.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between py-2 border-b border-pp-sand/30 last:border-0"
                      >
                        <div>
                          <p className="text-pp-ink">{item.product_name}</p>
                          <p className="text-sm text-pp-stone">
                            Qty: {item.quantity}
                          </p>
                        </div>
                        <p className="font-medium text-pp-ink">
                          {formatPrice(item.total_price)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Totals */}
                <div className="border-t border-pp-sand pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-pp-stone">Subtotal</span>
                    <span className="text-pp-charcoal">
                      {formatPrice(selectedOrder.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-pp-stone">Shipping</span>
                    <span className="text-pp-charcoal">
                      {formatPrice(selectedOrder.shipping_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between font-medium pt-2 border-t border-pp-sand">
                    <span className="text-pp-ink">Total</span>
                    <span className="text-pp-ink">
                      {formatPrice(selectedOrder.total)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  )
}
